<template>
  <div class="flex-1 max-w-lg mx-4 hidden md:block">
    <div class="relative">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
        </svg>
      </div>
      <input
        :value="searchQuery"
        @input="onInput"
        @keydown.enter="goFirstResult"
        type="text"
        placeholder="Rechercher un module..."
        class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors duration-200"
      />
      <div v-if="showDropdown" class="absolute mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
        <ul class="max-h-72 overflow-auto py-1">
          <li v-for="(item, i) in results" :key="item.to + i" @mousedown.prevent="navigate(item.to)"
              class="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer flex items-center space-x-2">
            <span class="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-gray-600">
              <svg v-if="item.icon === 'dashboard'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h7v7H3zM14 3h7v4h-7zM14 10h7v11h-7zM3 14h7v7H3z"/></svg>
              <svg v-else-if="item.icon === 'users'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M15 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
              <svg v-else-if="item.icon === 'employees'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A4 4 0 018 17h8a4 4 0 013 1.236M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <svg v-else-if="item.icon === 'payroll'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/></svg>
              <svg v-else-if="item.icon === 'documents'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h10M7 11h10M7 15h10M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z"/></svg>
              <svg v-else-if="item.icon === 'reports'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6a2 2 0 114 0v6m5 0h-14"/></svg>
              <svg v-else-if="item.icon === 'settings'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 0V4m0 16v-4m8-4h4M4 12H0m17.657-5.657l2.828-2.828M3.515 20.485l2.828-2.828M3.515 3.515l2.828 2.828M17.657 17.657l2.828 2.828"/></svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
            </span>
            <span class="truncate">{{ item.label }}</span>
          </li>
          <li v-if="results.length === 0" class="px-3 py-2 text-sm text-gray-400">Aucun résultat</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useGlobalSearch } from '@/composables/useGlobalSearch'
import type { SearchResultItem } from '@/composables/useGlobalSearch'
import { debounce } from '@/utils/debounce'
interface Props {
  searchQuery: string
}

defineProps<Props>()

const emit = defineEmits<{
  'update:searchQuery': [value: string]
}>()

const router = useRouter()
const { search } = useGlobalSearch()

const results = ref<SearchResultItem[]>([])
const showDropdown = ref(false)

const runSearch = (q: string) => {
  results.value = search(q, 10)
  showDropdown.value = q.trim().length > 0
}

const onInput = (e: Event) => {
  const v = (e.target as HTMLInputElement).value
  emit('update:searchQuery', v)
  debounced(v)
}

const debounced = debounce(runSearch, 200)

const navigate = (to: string) => {
  showDropdown.value = false
  router.push(to)
}

const goFirstResult = () => {
  if (results.value.length > 0) navigate(results.value[0].to)
}
</script>
